// Canonical academic taxonomy.
// Source: "list of subjects.xlsx" provided by client.
// Mirror of acanetic_web_be/src/data/academicTaxonomy.ts — keep in sync.

export const SUBJECT_CATEGORIES = [
  'Medicine and Dentistry',
  'Subjects Allied to Medicine',
  'Biological and Sport Sciences',
  'Veterinary Sciences',
  'Agriculture, Food and Related Studies',
  'Physical Sciences',
  'Mathematical Sciences',
  'Computing',
  'Engineering',
  'Architecture, Building and Planning',
  'Social Sciences',
  'Business and Management',
  'Law',
  'Education and Teaching',
  'Humanities and Liberal Arts',
  'Languages and Linguistics',
  'Creative Arts and Design',
  'Performing Arts',
  'Media, Communication and Journalism',
  'Combined / Interdisciplinary Subjects',
] as const;

export type SubjectCategory = (typeof SUBJECT_CATEGORIES)[number];

export interface Subject {
  name: string;
  category: SubjectCategory;
}

export const SUBJECTS: ReadonlyArray<Subject> = [
  // Medicine and Dentistry
  { name: 'Medicine (clinical)', category: 'Medicine and Dentistry' },
  { name: 'Medicine (pre-clinical)', category: 'Medicine and Dentistry' },
  { name: 'Dentistry', category: 'Medicine and Dentistry' },
  { name: 'Oral sciences', category: 'Medicine and Dentistry' },
  { name: 'Clinical dentistry', category: 'Medicine and Dentistry' },
  { name: 'Dental nursing', category: 'Medicine and Dentistry' },
  { name: 'Dental technology', category: 'Medicine and Dentistry' },

  // Subjects Allied to Medicine
  { name: 'Nursing', category: 'Subjects Allied to Medicine' },
  { name: 'Adult nursing', category: 'Subjects Allied to Medicine' },
  { name: 'Nursing - Children and Young People', category: 'Subjects Allied to Medicine' },
  { name: 'Public health nursing', category: 'Subjects Allied to Medicine' },
  { name: 'Midwifery', category: 'Subjects Allied to Medicine' },
  { name: 'Physiotherapy', category: 'Subjects Allied to Medicine' },
  { name: 'Occupational therapy', category: 'Subjects Allied to Medicine' },
  { name: 'Radiography', category: 'Subjects Allied to Medicine' },
  { name: 'Radiotherapy', category: 'Subjects Allied to Medicine' },
  { name: 'Advanced medical imaging', category: 'Subjects Allied to Medicine' },
  { name: 'Paramedic science', category: 'Subjects Allied to Medicine' },
  { name: 'Speech and language therapy', category: 'Subjects Allied to Medicine' },
  { name: 'Podiatry', category: 'Subjects Allied to Medicine' },
  { name: 'Dietetics', category: 'Subjects Allied to Medicine' },
  { name: 'Nutrition', category: 'Subjects Allied to Medicine' },
  { name: 'Ophthalmics / Optometry', category: 'Subjects Allied to Medicine' },
  { name: 'Pharmacology', category: 'Subjects Allied to Medicine' },
  { name: 'Pharmacy', category: 'Subjects Allied to Medicine' },
  { name: 'Complementary medicine', category: 'Subjects Allied to Medicine' },
  { name: 'Counselling', category: 'Subjects Allied to Medicine' },
  { name: 'Health sciences', category: 'Subjects Allied to Medicine' },
  { name: 'Public health', category: 'Subjects Allied to Medicine' },
  { name: 'Environmental Health', category: 'Subjects Allied to Medicine' },
  { name: 'Therapeutic arts', category: 'Subjects Allied to Medicine' },
  { name: 'Medical technology', category: 'Subjects Allied to Medicine' },
  { name: 'Medical biotechnology', category: 'Subjects Allied to Medicine' },

  // Biological and Sport Sciences
  { name: 'Biology', category: 'Biological and Sport Sciences' },
  { name: 'Biochemistry', category: 'Biological and Sport Sciences' },
  { name: 'Biotechnology', category: 'Biological and Sport Sciences' },
  { name: 'Applied biotechnology', category: 'Biological and Sport Sciences' },
  { name: 'Genetics', category: 'Biological and Sport Sciences' },
  { name: 'Microbiology', category: 'Biological and Sport Sciences' },
  { name: 'Molecular biology', category: 'Biological and Sport Sciences' },
  { name: 'Cell biology', category: 'Biological and Sport Sciences' },
  { name: 'Zoology', category: 'Biological and Sport Sciences' },
  { name: 'Botany / Plant sciences', category: 'Biological and Sport Sciences' },
  { name: 'Marine biology', category: 'Biological and Sport Sciences' },
  { name: 'Ecology', category: 'Biological and Sport Sciences' },
  { name: 'Environmental biology', category: 'Biological and Sport Sciences' },
  { name: 'Neuroscience', category: 'Biological and Sport Sciences' },
  { name: 'Biomedical sciences', category: 'Biological and Sport Sciences' },
  { name: 'Anatomy', category: 'Biological and Sport Sciences' },
  { name: 'Physiology', category: 'Biological and Sport Sciences' },
  { name: 'Sports science', category: 'Biological and Sport Sciences' },
  { name: 'Exercise science', category: 'Biological and Sport Sciences' },
  { name: 'Sports coaching', category: 'Biological and Sport Sciences' },
  { name: 'Sports therapy', category: 'Biological and Sport Sciences' },
  { name: 'Sports management', category: 'Biological and Sport Sciences' },
  { name: 'Physical education', category: 'Biological and Sport Sciences' },

  // Veterinary Sciences
  { name: 'Veterinary medicine', category: 'Veterinary Sciences' },
  { name: 'Veterinary nursing', category: 'Veterinary Sciences' },
  { name: 'Animal science', category: 'Veterinary Sciences' },
  { name: 'Animal behaviour', category: 'Veterinary Sciences' },
  { name: 'Animal health', category: 'Veterinary Sciences' },
  { name: 'Animal nutrition', category: 'Veterinary Sciences' },
  { name: 'Animal management', category: 'Veterinary Sciences' },

  // Agriculture, Food and Related Studies
  { name: 'Agriculture', category: 'Agriculture, Food and Related Studies' },
  { name: 'Crop production', category: 'Agriculture, Food and Related Studies' },
  { name: 'Soil science', category: 'Agriculture, Food and Related Studies' },
  { name: 'Horticulture', category: 'Agriculture, Food and Related Studies' },
  { name: 'Forestry', category: 'Agriculture, Food and Related Studies' },
  { name: 'Fisheries', category: 'Agriculture, Food and Related Studies' },
  { name: 'Food science', category: 'Agriculture, Food and Related Studies' },
  { name: 'Food technology', category: 'Agriculture, Food and Related Studies' },
  { name: 'Food safety', category: 'Agriculture, Food and Related Studies' },
  { name: 'Food nutrition', category: 'Agriculture, Food and Related Studies' },
  { name: 'Agricultural engineering', category: 'Agriculture, Food and Related Studies' },

  // Physical Sciences
  { name: 'Physics', category: 'Physical Sciences' },
  { name: 'Astronomy', category: 'Physical Sciences' },
  { name: 'Astrophysics', category: 'Physical Sciences' },
  { name: 'Chemistry', category: 'Physical Sciences' },
  { name: 'Materials science', category: 'Physical Sciences' },
  { name: 'Polymer science', category: 'Physical Sciences' },
  { name: 'Nanotechnology', category: 'Physical Sciences' },
  { name: 'Geology', category: 'Physical Sciences' },
  { name: 'Earth sciences', category: 'Physical Sciences' },
  { name: 'Geophysics', category: 'Physical Sciences' },
  { name: 'Geochemistry', category: 'Physical Sciences' },
  { name: 'Environmental science', category: 'Physical Sciences' },
  { name: 'Atmospheric science', category: 'Physical Sciences' },
  { name: 'Oceanography', category: 'Physical Sciences' },
  { name: 'Forensic science', category: 'Physical Sciences' },
  { name: 'Nautical science', category: 'Physical Sciences' },

  // Mathematical Sciences
  { name: 'Mathematics', category: 'Mathematical Sciences' },
  { name: 'Applied mathematics', category: 'Mathematical Sciences' },
  { name: 'Pure mathematics', category: 'Mathematical Sciences' },
  { name: 'Statistics', category: 'Mathematical Sciences' },
  { name: 'Actuarial science', category: 'Mathematical Sciences' },
  { name: 'Financial mathematics', category: 'Mathematical Sciences' },
  { name: 'Operational research', category: 'Mathematical Sciences' },
  { name: 'Data science', category: 'Mathematical Sciences' },

  // Computing
  { name: 'Computer science', category: 'Computing' },
  { name: 'Software engineering', category: 'Computing' },
  { name: 'Artificial intelligence (AI)', category: 'Computing' },
  { name: 'AI engineering', category: 'Computing' },
  { name: 'Machine learning', category: 'Computing' },
  { name: 'Data analytics', category: 'Computing' },
  { name: 'Cyber security', category: 'Computing' },
  { name: 'Information technology', category: 'Computing' },
  { name: 'Information systems', category: 'Computing' },
  { name: 'Computer networks', category: 'Computing' },
  { name: 'Human-computer interaction', category: 'Computing' },
  { name: 'Computer graphics', category: 'Computing' },
  { name: 'Games development', category: 'Computing' },
  { name: 'Games and digital technology', category: 'Computing' },
  { name: 'Robotics and AI', category: 'Computing' },

  // Engineering
  { name: 'General engineering', category: 'Engineering' },
  { name: 'Mechanical engineering', category: 'Engineering' },
  { name: 'Electrical engineering', category: 'Engineering' },
  { name: 'Electronic engineering', category: 'Engineering' },
  { name: 'Civil engineering', category: 'Engineering' },
  { name: 'Structural engineering', category: 'Engineering' },
  { name: 'Chemical engineering', category: 'Engineering' },
  { name: 'Aerospace engineering', category: 'Engineering' },
  { name: 'Automotive engineering', category: 'Engineering' },
  { name: 'Marine engineering', category: 'Engineering' },
  { name: 'Materials engineering', category: 'Engineering' },
  { name: 'Mechatronics', category: 'Engineering' },
  { name: 'Robotics', category: 'Engineering' },
  { name: 'Industrial engineering', category: 'Engineering' },
  { name: 'Manufacturing engineering', category: 'Engineering' },
  { name: 'Energy engineering', category: 'Engineering' },
  { name: 'Renewable energy engineering', category: 'Engineering' },
  { name: 'Biomedical engineering', category: 'Engineering' },
  { name: 'Bioengineering', category: 'Engineering' },
  { name: 'Engineering management', category: 'Engineering' },

  // Architecture, Building and Planning
  { name: 'Architecture', category: 'Architecture, Building and Planning' },
  { name: 'Architectural technology', category: 'Architecture, Building and Planning' },
  { name: 'Building surveying', category: 'Architecture, Building and Planning' },
  { name: 'Construction', category: 'Architecture, Building and Planning' },
  { name: 'Construction management', category: 'Architecture, Building and Planning' },
  { name: 'Quantity surveying', category: 'Architecture, Building and Planning' },
  { name: 'Real estate', category: 'Architecture, Building and Planning' },
  { name: 'Urban planning', category: 'Architecture, Building and Planning' },
  { name: 'Town planning', category: 'Architecture, Building and Planning' },
  { name: 'Regional planning', category: 'Architecture, Building and Planning' },
  { name: 'Landscape architecture', category: 'Architecture, Building and Planning' },

  // Social Sciences
  { name: 'Economics', category: 'Social Sciences' },
  { name: 'Econometrics', category: 'Social Sciences' },
  { name: 'Political science', category: 'Social Sciences' },
  { name: 'Politics', category: 'Social Sciences' },
  { name: 'International relations', category: 'Social Sciences' },
  { name: 'Sociology', category: 'Social Sciences' },
  { name: 'Anthropology', category: 'Social Sciences' },
  { name: 'Psychology', category: 'Social Sciences' },
  { name: 'Human geography', category: 'Social Sciences' },
  { name: 'Development studies', category: 'Social Sciences' },
  { name: 'Social policy', category: 'Social Sciences' },
  { name: 'Social work', category: 'Social Sciences' },
  { name: 'Social care', category: 'Social Sciences' },
  { name: 'Criminology', category: 'Social Sciences' },
  { name: 'Demography', category: 'Social Sciences' },
  { name: 'Public administration', category: 'Social Sciences' },
  { name: 'Public policy', category: 'Social Sciences' },
  { name: 'Public services', category: 'Social Sciences' },

  // Business and Management
  { name: 'Business studies', category: 'Business and Management' },
  { name: 'Management', category: 'Business and Management' },
  { name: 'Leadership', category: 'Business and Management' },
  { name: 'Leadership and management', category: 'Business and Management' },
  { name: 'Project management', category: 'Business and Management' },
  { name: 'Marketing', category: 'Business and Management' },
  { name: 'Accounting', category: 'Business and Management' },
  { name: 'Finance', category: 'Business and Management' },
  { name: 'Banking', category: 'Business and Management' },
  { name: 'International business', category: 'Business and Management' },
  { name: 'Entrepreneurship', category: 'Business and Management' },
  { name: 'Human resource management', category: 'Business and Management' },
  { name: 'Operations management', category: 'Business and Management' },
  { name: 'Supply chain management', category: 'Business and Management' },
  { name: 'Hospitality management', category: 'Business and Management' },
  { name: 'Tourism management', category: 'Business and Management' },
  { name: 'Tourism and hospitality management', category: 'Business and Management' },
  { name: 'International tourism', category: 'Business and Management' },
  { name: 'Event management', category: 'Business and Management' },

  // Law
  { name: 'Law (general)', category: 'Law' },
  { name: 'Commercial law', category: 'Law' },
  { name: 'International law', category: 'Law' },
  { name: 'Criminal law', category: 'Law' },
  { name: 'Constitutional law', category: 'Law' },
  { name: 'Maritime law', category: 'Law' },
  { name: 'Intellectual property law', category: 'Law' },
  { name: 'Human rights law', category: 'Law' },

  // Education and Teaching
  { name: 'Education studies', category: 'Education and Teaching' },
  { name: 'Teaching', category: 'Education and Teaching' },
  { name: 'Early childhood education', category: 'Education and Teaching' },
  { name: 'Childhood studies', category: 'Education and Teaching' },
  { name: 'Primary education', category: 'Education and Teaching' },
  { name: 'Secondary education', category: 'Education and Teaching' },
  { name: 'Special education', category: 'Education and Teaching' },
  { name: 'Educational leadership', category: 'Education and Teaching' },
  { name: 'Curriculum studies', category: 'Education and Teaching' },
  { name: 'Literacy', category: 'Education and Teaching' },
  { name: 'TESOL (Teaching English to Speakers of Other Languages)', category: 'Education and Teaching' },

  // Humanities and Liberal Arts
  { name: 'History', category: 'Humanities and Liberal Arts' },
  { name: 'Ancient history', category: 'Humanities and Liberal Arts' },
  { name: 'Archaeology', category: 'Humanities and Liberal Arts' },
  { name: 'Philosophy', category: 'Humanities and Liberal Arts' },
  { name: 'Ethics', category: 'Humanities and Liberal Arts' },
  { name: 'Theology', category: 'Humanities and Liberal Arts' },
  { name: 'Religious studies', category: 'Humanities and Liberal Arts' },
  { name: 'Classics', category: 'Humanities and Liberal Arts' },
  { name: 'Cultural studies', category: 'Humanities and Liberal Arts' },
  { name: 'Heritage studies', category: 'Humanities and Liberal Arts' },
  { name: 'Library and information studies', category: 'Humanities and Liberal Arts' },

  // Languages and Linguistics
  { name: 'English language', category: 'Languages and Linguistics' },
  { name: 'Linguistics', category: 'Languages and Linguistics' },
  { name: 'Translation studies', category: 'Languages and Linguistics' },
  { name: 'Interpreting', category: 'Languages and Linguistics' },
  { name: 'Comparative literature', category: 'Languages and Linguistics' },
  { name: 'Creative writing', category: 'Languages and Linguistics' },
  { name: 'Modern languages', category: 'Languages and Linguistics' },
  { name: 'French studies', category: 'Languages and Linguistics' },
  { name: 'Spanish studies', category: 'Languages and Linguistics' },
  { name: 'German studies', category: 'Languages and Linguistics' },
  { name: 'Italian studies', category: 'Languages and Linguistics' },
  { name: 'Arabic studies', category: 'Languages and Linguistics' },
  { name: 'Chinese studies', category: 'Languages and Linguistics' },
  { name: 'Japanese studies', category: 'Languages and Linguistics' },
  { name: 'Russian studies', category: 'Languages and Linguistics' },

  // Creative Arts and Design
  { name: 'Fine art', category: 'Creative Arts and Design' },
  { name: 'Painting', category: 'Creative Arts and Design' },
  { name: 'Sculpture', category: 'Creative Arts and Design' },
  { name: 'Photography', category: 'Creative Arts and Design' },
  { name: 'Illustration', category: 'Creative Arts and Design' },
  { name: 'Graphic design', category: 'Creative Arts and Design' },
  { name: 'Product design', category: 'Creative Arts and Design' },
  { name: 'Industrial design', category: 'Creative Arts and Design' },
  { name: 'Fashion design', category: 'Creative Arts and Design' },
  { name: 'Textile design', category: 'Creative Arts and Design' },
  { name: 'Costume design', category: 'Creative Arts and Design' },
  { name: 'Interior design', category: 'Creative Arts and Design' },
  { name: 'Animation', category: 'Creative Arts and Design' },
  { name: 'Game art', category: 'Creative Arts and Design' },
  { name: 'Digital design', category: 'Creative Arts and Design' },

  // Performing Arts
  { name: 'Music', category: 'Performing Arts' },
  { name: 'Music performance', category: 'Performing Arts' },
  { name: 'Music technology', category: 'Performing Arts' },
  { name: 'Composition', category: 'Performing Arts' },
  { name: 'Theatre studies', category: 'Performing Arts' },
  { name: 'Acting', category: 'Performing Arts' },
  { name: 'Drama', category: 'Performing Arts' },
  { name: 'Dance', category: 'Performing Arts' },
  { name: 'Performance arts', category: 'Performing Arts' },

  // Media, Communication and Journalism
  { name: 'Journalism', category: 'Media, Communication and Journalism' },
  { name: 'Media studies', category: 'Media, Communication and Journalism' },
  { name: 'Film studies', category: 'Media, Communication and Journalism' },
  { name: 'Television production', category: 'Media, Communication and Journalism' },
  { name: 'Digital video production', category: 'Media, Communication and Journalism' },
  { name: 'Broadcasting', category: 'Media, Communication and Journalism' },
  { name: 'Communication studies', category: 'Media, Communication and Journalism' },
  { name: 'Digital media', category: 'Media, Communication and Journalism' },
  { name: 'Advertising', category: 'Media, Communication and Journalism' },
  { name: 'Public relations', category: 'Media, Communication and Journalism' },

  // Combined / Interdisciplinary Subjects
  { name: 'Liberal arts', category: 'Combined / Interdisciplinary Subjects' },
  { name: 'Environmental studies', category: 'Combined / Interdisciplinary Subjects' },
  { name: 'Sustainability', category: 'Combined / Interdisciplinary Subjects' },
  { name: 'Cognitive science', category: 'Combined / Interdisciplinary Subjects' },
  { name: 'Digital humanities', category: 'Combined / Interdisciplinary Subjects' },
  { name: 'Gender studies', category: 'Combined / Interdisciplinary Subjects' },
  { name: 'Peace studies', category: 'Combined / Interdisciplinary Subjects' },
  { name: 'Global studies', category: 'Combined / Interdisciplinary Subjects' },
];

export const SUBJECT_NAMES: ReadonlyArray<string> = SUBJECTS.map((s) => s.name);

// Academic role types (from xlsx "List of jobs" section, cleaned).
// Used as a multi-select on the candidate side ("I'm available as...")
// and as a single-select on job postings (the specific role being filled).
export const ACADEMIC_ROLES = [
  'External Examiner',
  'External advisor',
  'External panel member',
  'PhD examiner',
  'End point assessor',
  'Hourly Paid Lecturer',
] as const;

export type AcademicRole = (typeof ACADEMIC_ROLES)[number];

// Study levels / qualification levels.
// Multi-select on both candidate ("I teach at...") and job ("This role covers...").
export const STUDY_LEVELS = [
  'HNC',
  'HND',
  'Apprenticeship',
  'BA',
  'BSc',
  'BEng',
  'PGCert',
  'PGCE',
  'MA',
  'MSc',
  'MEng',
  'MBA',
  'MPhil',
  'PhD',
  'CPD',
] as const;

export type StudyLevel = (typeof STUDY_LEVELS)[number];
